import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Save } from "lucide-react";
import { Label } from "@/components/ui/label";

interface SocialLink {
  id: string;
  platform_name: string;
  platform_url: string;
  icon_name: string;
  is_active: boolean;
  sort_order: number;
}

export const SocialLinksManager = () => {
  const [links, setLinks] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const { data, error } = await supabase
        .from("social_media_links")
        .select("*")
        .order("sort_order");

      if (error) throw error;
      setLinks(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addNewLink = () => {
    const newLink: SocialLink = {
      id: crypto.randomUUID(),
      platform_name: "",
      platform_url: "",
      icon_name: "Link",
      is_active: true,
      sort_order: links.length,
    };
    setLinks([...links, newLink]);
  };

  const updateLink = (id: string, field: keyof SocialLink, value: any) => {
    setLinks(links.map(link =>
      link.id === id ? { ...link, [field]: value } : link
    ));
  };

  const saveLink = async (link: SocialLink) => {
    try {
      const { data: profileData } = await supabase
        .from("photographer_profiles")
        .select("id")
        .single();

      if (!profileData) {
        toast({
          title: "Error",
          description: "Profile not found",
          variant: "destructive",
        });
        return;
      }

      const linkData = {
        photographer_id: profileData.id,
        platform_name: link.platform_name,
        platform_url: link.platform_url,
        icon_name: link.icon_name,
        is_active: link.is_active,
        sort_order: link.sort_order,
      };

      const { error } = await supabase
        .from("social_media_links")
        .upsert({ id: link.id, ...linkData });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Social link saved successfully",
      });

      fetchLinks();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteLink = async (id: string) => {
    try {
      const { error } = await supabase
        .from("social_media_links")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setLinks(links.filter(link => link.id !== id));
      toast({
        title: "Success",
        description: "Social link deleted successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl md:text-2xl font-bold">Social Media Links</h2>
        <Button onClick={addNewLink} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Link
        </Button>
      </div>

      <div className="grid gap-4">
        {links.map((link) => (
          <Card key={link.id}>
            <CardHeader>
              <CardTitle className="text-base md:text-lg flex items-center justify-between">
                <span>{link.platform_name || "New Social Link"}</span>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={link.is_active}
                    onCheckedChange={(checked) => updateLink(link.id, "is_active", checked)}
                  />
                  {hasPermission('delete_social') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteLink(link.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Platform Name</Label>
                  <Input
                    placeholder="e.g., Instagram"
                    value={link.platform_name}
                    onChange={(e) => updateLink(link.id, "platform_name", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Icon Name (Lucide)</Label>
                  <Input
                    placeholder="e.g., FaInstagram, FaFacebook, FaWhatsapp"
                    value={link.icon_name}
                    onChange={(e) => updateLink(link.id, "icon_name", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Platform URL</Label>
                <Input
                  placeholder="https://..."
                  value={link.platform_url}
                  onChange={(e) => updateLink(link.id, "platform_url", e.target.value)}
                />
              </div>
              <div>
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={link.sort_order}
                  onChange={(e) => updateLink(link.id, "sort_order", parseInt(e.target.value))}
                />
              </div>
              <Button onClick={() => saveLink(link)} className="w-full">
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
